import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { IoIosClose, IoIosArrowBack } from 'react-icons/io';
import { IoPlay, IoPause } from 'react-icons/io5';
import { MdOutlineFullscreen, MdOutlineFullscreenExit } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import 'twin.macro';
import tw from 'twin.macro';
import NormalModal, { useModalDragHandle } from '../../normal-modal';
import { usePrefetchedMediaSrc } from '../../../hooks';
import { exerciseType as EXERCISE_TYPE } from '../../../constants/constants';

// Same stripping rule as resolveExerciseType in features/exercise/helpers.
// Kept local rather than imported: that module pulls in @mediapipe/tasks-vision,
// which has no business in this modal's module graph (see info-bar.jsx for the
// same reasoning).
const normalizeToken = value =>
  String(value ?? '')
    .replace(/[\s_-]+/g, '')
    .toLowerCase();

const EXERCISE_TYPE_TOKENS = {
  repbased: EXERCISE_TYPE.repBased,
  timedreps: EXERCISE_TYPE.timedReps,
  timedhold: EXERCISE_TYPE.timedHold,
};

// The backend doesn't always state `type` (see exercise-details-modal bug
// reports where pauseStage entries carry only `sets`/`rest_seconds`/
// `set_eta_seconds`). Per the prescription policy, `reps` is required for
// REP_BASED/TIMED_REPS and refused for the single_rep movement that always
// runs as TIMED_HOLD — so a missing `type` with no `reps` can only be a
// TIMED_HOLD, which is a safer default than the generic REP_BASED fallback
// used at runtime (where the backend does always state `type`).
const resolveExerciseType = (exercise, exerciseConfig) => {
  const stated = EXERCISE_TYPE_TOKENS[normalizeToken(exercise?.type ?? exerciseConfig?.type)];
  if (stated) {
    return stated;
  }
  return exerciseConfig?.reps != null ? EXERCISE_TYPE.repBased : EXERCISE_TYPE.timedHold;
};

const DragHandle = () => {
  const dragControls = useModalDragHandle();

  return (
    <div
      tw="hidden justify-center py-[8px] cursor-grab touch-none"
      css={tw`max-lg:flex`}
      onPointerDown={event => dragControls?.start(event)}
    >
      <div tw="w-[36px] h-[4px] rounded-full bg-[#D9DFE4]" />
    </div>
  );
};

const ExerciseDetailsModal = ({ open, onClose, exercise }) => {
  const { t } = useTranslation('exercise');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      return;
    }
    if (isPlaying) {
      videoEl.play().catch(() => {});
    } else {
      videoEl.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (!open) {
      setIsFullscreen(false);
      setIsPlaying(false);
    }
  }, [open]);

  // Ahead of the `!exercise` bail below (Rules of Hooks) — both fall back to
  // `undefined` unresolved when there's no exercise yet, same as the raw
  // values would have. Resolves to a prefetched blob: URL when
  // camera-permission already warmed this session's media and it hasn't
  // expired, otherwise to the original remote URL unchanged.
  const resolvedVideoSrc = usePrefetchedMediaSrc(
    exercise?.tutorialVideoUrl || exercise?.referenceVideoUrl
  );
  const resolvedPosterSrc = usePrefetchedMediaSrc(exercise?.imageUrl);

  if (!exercise) {
    return null;
  }

  const exerciseConfig = exercise.exerciseConfig || {};
  const resolvedType = resolveExerciseType(exercise, exerciseConfig);
  const isTimedHold = resolvedType === EXERCISE_TYPE.timedHold;
  const isRepBased = resolvedType === EXERCISE_TYPE.repBased;

  const parameterTiles = [
    {
      label: t('exerciseDetailsModal.parameterSets'),
      value: exerciseConfig.sets ?? exercise.totalSets,
    },
    // Reps are shown for REP_BASED only. TIMED_HOLD has none by policy — `reps`
    // is refused for the single_rep movement that always runs as this type, so
    // any value reaching here is stale or a backend default. TIMED_REPS does
    // carry a prescribed reps figure, but the set is bounded by its duration,
    // which is the parameter that actually governs it — so the count is left
    // out there too rather than reading as a second, competing target.
    isRepBased
      ? {
          label: t('exerciseDetailsModal.parameterRepetitions'),
          value: exerciseConfig.reps,
        }
      : null,
    // REP_BASED is the one type with no time budget to show: the athlete
    // finishes the set at their own pace, so its `set_eta_seconds` is an
    // estimate that reads as a prescription it isn't. The other two do show
    // it off `set_eta_seconds`, and only the label differs: TIMED_HOLD's hold
    // *is* the set, so it reads as "Hold Duration"; TIMED_REPS reads as "Set
    // Duration".
    isRepBased
      ? null
      : {
          label: t(
            isTimedHold
              ? 'exerciseDetailsModal.parameterHoldDuration'
              : 'exerciseDetailsModal.parameterSetDuration'
          ),
          value: exerciseConfig.set_eta_seconds,
          unit: t('exerciseDetailsModal.secondsUnit'),
        },
    {
      // Backend field is `rest_seconds`, not `rest`.
      label: t('exerciseDetailsModal.parameterRestBetweenSets'),
      value: exerciseConfig.rest_seconds,
      unit: t('exerciseDetailsModal.secondsUnit'),
    },
  ]
    .filter(Boolean)
    .filter(tile => tile.value != null && tile.value !== 0);

  const togglePlay = e => {
    e.stopPropagation();
    setIsPlaying(prev => !prev);
  };

  const handleClose = () => {
    setIsFullscreen(false);
    setIsPlaying(false);
    onClose?.();
  };

  const videoBlock = (
    <div
      css={[
        tw`relative bg-black rounded-[12px] border border-[#BDC4C9] overflow-hidden shrink-0`,
        isFullscreen
          ? tw`w-full h-full rounded-none border-0`
          : tw`w-[384px] h-[225px] max-lg:w-full max-lg:h-auto max-lg:aspect-video`,
      ]}
    >
      <video
        preload="metadata"
        src={resolvedVideoSrc}
        poster={resolvedPosterSrc}
        playsInline
        loop
        tw="object-contain w-full h-full"
        onClick={togglePlay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        ref={videoRef}
      />
      <button
        tw="flex absolute top-1/2 left-1/2 justify-center items-center bg-white rounded-full -translate-x-1/2 -translate-y-1/2 cursor-pointer size-[46px]"
        onClick={togglePlay}
      >
        {isPlaying ? (
          <IoPause size={22} tw="text-[#2E2E2E]" />
        ) : (
          <IoPlay size={22} tw="text-[#2E2E2E] translate-x-[1px]" />
        )}
      </button>
      <button
        tw="flex absolute right-[19px] bottom-[19px] justify-center items-center text-white cursor-pointer"
        onClick={e => {
          e.stopPropagation();
          setIsFullscreen(prev => !prev);
        }}
      >
        {isFullscreen ? <MdOutlineFullscreenExit size={24} /> : <MdOutlineFullscreen size={24} />}
      </button>
    </div>
  );

  return (
    <NormalModal
      open={open}
      handleClose={handleClose}
      hideIcon
      backgroundModal="rgba(0,0,0,0.25)"
      dragToClose
      containerModalStyle={tw`w-[856px] max-w-[90vw] h-fit rounded-[12px]! overflow-visible
        max-lg:w-full! max-lg:max-w-none! max-lg:h-fit! max-lg:max-h-[80vh]! max-lg:max-h-[80dvh]! max-lg:max-h-[80svh]! max-lg:rounded-t-[20px]! max-lg:rounded-b-none! max-lg:overflow-y-auto!`}
      content={
        <div>
          <div tw="sticky top-0 z-10 bg-white rounded-t-[12px]" css={tw`max-lg:rounded-t-[20px]`}>
            <DragHandle />
            <div tw="flex gap-[12px] items-center py-[14px] pr-[14px] pl-[32px] w-full border-b border-[#D9DFE4]">
              {isFullscreen && (
                <button
                  tw="flex justify-center items-center rounded-full cursor-pointer shrink-0 size-[24px]"
                  onClick={() => setIsFullscreen(false)}
                >
                  <IoIosArrowBack size={24} tw="text-[#2E2E2E] rtl:scale-x-[-1]" />
                </button>
              )}
              <p tw="flex-1 text-[18px] font-bold text-[#2E2E2E]">{exercise.exerciseName}</p>
              <button
                tw="flex justify-center items-center rounded-full cursor-pointer shrink-0 size-[50px]"
                css={tw`max-lg:hidden`}
                onClick={handleClose}
              >
                <IoIosClose size={30} tw="text-[#2E2E2E]" />
              </button>
            </div>
          </div>

          {isFullscreen ? (
            <div tw="flex p-[32px] w-full h-[400px]" css={tw`max-lg:p-[16px] max-lg:h-[240px]`}>
              {videoBlock}
            </div>
          ) : (
            <>
              <div
                tw="flex gap-[32px] items-start p-[32px] w-full"
                css={tw`max-lg:flex-col max-lg:gap-[16px] max-lg:p-[16px]`}
              >
                {videoBlock}
                <div
                  tw="flex flex-col flex-1 gap-[12px] min-w-0 max-h-[273px] overflow-y-auto w-full"
                  css={tw`max-lg:max-h-none max-lg:overflow-visible`}
                >
                  <div tw="flex flex-col gap-[6px] w-full">
                    <p tw="text-[18px] font-medium text-[#2E2E2E]">
                      {t('exerciseDetailsModal.description')}
                    </p>
                    <p tw="text-[14px] text-[#656565] leading-[1.43]">{exercise.description}</p>
                  </div>
                  <div tw="flex flex-col gap-[6px] pb-[10px] w-full">
                    <p tw="text-[18px] font-medium text-[#2E2E2E]">
                      {t('exerciseDetailsModal.instructions')}
                    </p>
                    {/* Backend sends this field as HTML, not plain text. */}
                    <div
                      tw="text-[14px] text-[#656565] leading-[1.43]"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(exercise.instructions ?? ''),
                      }}
                    />
                  </div>
                </div>
              </div>

              {parameterTiles.length > 0 && (
                <div
                  tw="flex flex-col gap-[24px] px-[32px] pb-[32px] w-full"
                  css={tw`max-lg:px-[16px] max-lg:pb-[16px]`}
                >
                  <p tw="text-[18px] font-medium text-[#656565]">
                    {t('exerciseDetailsModal.exerciseParameters')}
                  </p>
                  <div
                    tw="flex gap-[32px] items-center w-full"
                    css={tw`max-lg:grid max-lg:grid-cols-2 max-lg:gap-x-[12px] max-lg:gap-y-[20px]`}
                  >
                    {parameterTiles.map(tile => (
                      <div
                        key={tile.label}
                        tw="flex flex-col flex-1 gap-[4px] items-center py-[10px] min-w-0"
                        css={tw`max-lg:items-start max-lg:py-0`}
                      >
                        <p
                          tw="text-[14px] font-medium text-[#656565] whitespace-nowrap"
                          css={tw`max-lg:whitespace-normal max-lg:text-[13px]`}
                        >
                          {tile.label}
                        </p>
                        <div tw="flex gap-[4px] items-center">
                          <p
                            tw="text-[32px] font-semibold leading-[1.2] text-[#2E2E2E]"
                            css={tw`max-lg:text-[24px]`}
                          >
                            {tile.value}
                          </p>
                          {tile.unit && <p tw="text-[12px] text-[#656565]">{tile.unit}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      }
    />
  );
};

export default ExerciseDetailsModal;
